import React, { useRef, useState, useMemo, useEffect, useCallback } from "react";
import { Virtuoso } from "react-virtuoso";
import MessageItem from "./MessageItem";
import { buildQuotedLookupIndex } from "../../utils/messageThreadHints";
import { Fab, Fade } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

/** Alinhamento: cliente → esquerda; qualquer usuário (logado ou outro atendente) → direita. */
const getMessageAlignment = (message, loggedUser) => {
  if (!message) return "flex-end";
  const senderType = (message?.senderType || "").toLowerCase();
  const sender = (message?.sender || "").toLowerCase();
  const isClient =
    sender === "cliente" ||
    sender === "client" ||
    senderType === "client" ||
    senderType === "customer" ||
    message?.channel === "customer";
  const loggedUserId = loggedUser?.id != null ? String(loggedUser.id) : null;
  const msgUserId =
    message?.senderId != null
      ? String(message.senderId)
      : message?.id_usuario != null
      ? String(message.id_usuario)
      : null;
  const isAgentOrOperator =
    sender === "agent" ||
    sender === "operator" ||
    senderType === "agent" ||
    senderType === "operator";
  const isWhatsappAppOutbound = senderType === "whatsapp_app";
  const isUser =
    !!loggedUser &&
    (isAgentOrOperator || (!!loggedUserId && !!msgUserId && msgUserId === loggedUserId));
  if (message.type === "log" || message.type === "note") return "center";
  if (isWhatsappAppOutbound) return "flex-end";
  if (isUser) return "flex-end";
  return "flex-start";
};

const getMessageDate = (message) => {
  if (!message) return null;

  const raw =
    message.updatedAt ||
    message.lastUpdate ||
    message.timestamp ||
    message.createdAt;

  if (!raw) return null;

  // Formato MySQL "YYYY-MM-DD HH:mm:ss" vindo do backend (xpro: America/Araguaina)
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    const mysqlRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

    if (mysqlRegex.test(trimmed)) {
      // Interpretamos sem 'Z' para não aplicar conversão automática de fuso
      const d = new Date(trimmed.replace(" ", "T"));
      return isNaN(d.getTime()) ? null : d;
    }
  }

  // Datas já como Date ou ISO
  if (raw instanceof Date) {
    return isNaN(raw.getTime()) ? null : raw;
  }

  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

const MessageList = ({
  messages = [],
  loggedUser,
  onSendMessage,
  scrollBehavior = "smooth",
  defaultChannel,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  onDeleteMessage,
}) => {
  const virtuosoRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [initialAutoScrollDone, setInitialAutoScrollDone] = useState(false);

  // 🔄 Garante ordem cronológica: por data/hora e, quando iguais, por id (leadsmensagens autoincrement — menor = mais antigo)
  const orderedMessages = useMemo(() => {
    const list = Array.isArray(messages) ? [...messages] : [];
    const idA = (m) => (typeof m?.id === "number" ? m.id : Number(m?.id) || 0);

    list.sort((a, b) => {
      const da = getMessageDate(a);
      const db = getMessageDate(b);

      if (da && db) {
        const byDate = da - db;
        if (byDate !== 0) return byDate;
        return idA(a) - idA(b);
      }
      if (da && !db) return 1;
      if (!da && db) return -1;

      return idA(a) - idA(b);
    });

    return list;
  }, [messages]);

  const quotedLookup = useMemo(() => buildQuotedLookupIndex(orderedMessages), [orderedMessages]);

  const scrollToMessageId = useCallback(
    (targetId) => {
      const tid = Number(targetId);
      if (!Number.isFinite(tid)) return;
      const idx = orderedMessages.findIndex((m) => Number(m?.id) === tid);
      if (idx < 0 || !virtuosoRef.current) return;
      virtuosoRef.current.scrollToIndex({
        index: idx,
        align: "center",
        behavior: "smooth",
      });
    },
    [orderedMessages]
  );

  const scrollToBottom = (behavior = "smooth") => {
    if (virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({
        index: orderedMessages.length - 1,
        align: "end",
        behavior,
      });
    }
  };

  // 🔽 Ao abrir a conversa, posiciona automaticamente na mensagem mais recente.
  // Quando as mensagens carregam via API (após abrir os detalhes), o Virtuoso pode
  // ainda não ter renderizado os itens no mesmo ciclo; usamos dois frames (rAF após
  // rAF) para garantir que o layout da lista virtualizada já esteja pronto.
  useEffect(() => {
    if (!initialAutoScrollDone && orderedMessages.length > 0) {
      let cancelled = false;
      const id1 = requestAnimationFrame(() => {
        if (cancelled) return;
        requestAnimationFrame(() => {
          if (cancelled) return;
          scrollToBottom("auto");
          setInitialAutoScrollDone(true);
        });
      });
      return () => {
        cancelled = true;
        cancelAnimationFrame(id1);
      };
    }
  }, [orderedMessages.length, initialAutoScrollDone]);

  return (
    <div style={{ position: "relative", flexGrow: 1, height: "100%" }}>
      <Virtuoso
        ref={virtuosoRef}
        data={orderedMessages}
        followOutput={(isBottom) => (isBottom ? scrollBehavior : false)}
        startReached={() => {
          if (hasMore && !isLoadingMore && typeof onLoadMore === "function") {
            onLoadMore();
          }
        }}
        itemContent={(index, message) => {
          const justifyContent = getMessageAlignment(message, loggedUser);
          return (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent,
              }}
            >
              <MessageItem
                index={index}
                message={message}
                user={loggedUser}
                onSendMessage={onSendMessage}
                defaultChannel={defaultChannel}
                quotedLookup={quotedLookup}
                onNavigateToQuotedMessage={scrollToMessageId}
                onDeleteMessage={onDeleteMessage}
                conversationMessages={orderedMessages}
                messageIndex={index}
              />
            </div>
          );
        }}
        atBottomStateChange={setIsAtBottom}
        style={{ height: "100%", width: "100%", padding: "0 0 12px 0" }}
      />

      <Fade in={!isAtBottom}>
        <Fab
          color="primary"
          size="small"
          onClick={() => scrollToBottom("smooth")}
          style={{
            position: "absolute",
            bottom: 16,
            right: 16,
            zIndex: 10,
          }}
        >
          <ArrowDownwardIcon />
        </Fab>
      </Fade>
    </div>
  );
};

export default MessageList;
